import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calculator, Check, Download, FileText, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AppHeader } from "../components/AppHeader";
import { useAuth } from "../context/AuthContext";
import { PiecesTable } from "../components/PiecesTable";
import { PieceDialog } from "../components/PieceDialog";
import { ModulesGallery } from "../components/ModulesGallery";
import { CutPlan } from "../components/CutPlan";
import { PlanModePicker } from "../components/PlanModePicker";
import { LabelsView } from "../components/LabelsView";
import { BudgetDialog } from "../components/BudgetDialog";
import { BudgetStatus } from "../components/BudgetStatus";
import { api, downloadFile, errorMessage, pickSaveTarget } from "../lib/api";
import { CLIENT_LOCKED, STATUS, StatusBadge, statusOf } from "../components/StatusBadge";
import { THICKNESSES, newPiece } from "../lib/pieces";

const slug = (s) => (s || "projeto").replace(/[^A-Za-z0-9_-]+/g, "_");

const DefaultsBar = ({ defaults, onChange }) => (
  <div className="rounded-xl border border-[#2D333F] bg-[#181B21] p-4 grid sm:grid-cols-[140px_1fr_1fr] gap-3 items-end">
    <div className="sm:col-span-3 -mb-1">
      <p className="font-mono text-[10px] uppercase tracking-wider text-[#6B7280]">Padrões do ambiente (usados em novas peças e módulos)</p>
    </div>
    <Select value={String(defaults.thickness)} onValueChange={(v) => onChange({ ...defaults, thickness: Number(v) })}>
      <SelectTrigger data-testid="default-thickness-select" className="bg-[#0F1115] border-[#2D333F]"><SelectValue /></SelectTrigger>
      <SelectContent>{THICKNESSES.map((t) => <SelectItem key={t} value={String(t)}>MDF {t} mm</SelectItem>)}</SelectContent>
    </Select>
    <Input data-testid="default-mdf-color-input" value={defaults.mdf_color} onChange={(e) => onChange({ ...defaults, mdf_color: e.target.value })} placeholder="Cor do MDF" className="bg-[#0F1115] border-[#2D333F]" />
    <Input data-testid="default-edge-color-input" value={defaults.edge_color} onChange={(e) => onChange({ ...defaults, edge_color: e.target.value })} placeholder="Cor da fita de borda" className="bg-[#0F1115] border-[#2D333F]" />
  </div>
);

export default function ProjectEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState("saved");
  const [tab, setTab] = useState("modulos");
  const [cleared, setCleared] = useState(null);
  const clearedRef = useRef(null);
  clearedRef.current = cleared;
  const [labelLogo, setLabelLogo] = useState(() => localStorage.getItem("label_logo") !== "0");
  const toggleLogo = (on) => { setLabelLogo(on); localStorage.setItem("label_logo", on ? "1" : "0"); };
  const [editing, setEditing] = useState(null);
  const [plan, setPlan] = useState({ result: null, loading: false, error: "", key: "" });
  const [busy, setBusy] = useState("");
  const [budgetOpen, setBudgetOpen] = useState(false);
  const timer = useRef(null);
  const latest = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    let active = true;
    api.get(`/projects/${id}`)
      .then((r) => { if (active) setProject((o) => o || r.data); })
      .catch(() => { if (active) { toast.error("Ambiente não encontrado"); nav("/"); } });
    return () => { active = false; };
  }, [id, nav]);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    dirty.current = false;
    const p = latest.current;
    if (!p) return;
    setStatus("saving");
    try {
      const r = await api.put(`/projects/${id}`, { name: p.name || "Ambiente", client: p.client, notes: p.notes, defaults: p.defaults, pieces: p.pieces, plan_mode: p.plan_mode || "best", sheet_modes: p.sheet_modes || {} });
      setProject((o) => (o && (o.status !== r.data.status || o.budget_outdated !== r.data.budget_outdated)
        ? { ...o, status: r.data.status, completed: r.data.completed, budget_outdated: r.data.budget_outdated } : o));
      if (latest.current) latest.current = { ...latest.current, status: r.data.status, completed: r.data.completed, budget_outdated: r.data.budget_outdated };
      setStatus("saved");
    } catch (e) {
      setStatus("error");
      toast.error(await errorMessage(e));
      throw e;
    }
  }, [id]);

  useEffect(() => {
    const flush = () => { if (dirty.current) save().catch(() => {}); };
    window.addEventListener("beforeunload", flush);
    return () => { window.removeEventListener("beforeunload", flush); flush(); };
  }, [save]);

  const update = (patch) => {
    if (CLIENT_LOCKED.includes(latest.current?.status) && !isAdmin) return;
    setProject((o) => {
      const n = { ...o, ...patch };
      latest.current = n;
      return n;
    });
    setStatus("dirty");
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save().catch(() => {}), 900);
  };

  const pieces = project?.pieces || [];
  const planMode = project?.plan_mode || "best";
  const sheetModes = project?.sheet_modes || {};
  const piecesKey = JSON.stringify(pieces) + planMode + JSON.stringify(sheetModes);

  useEffect(() => {
    if (!project || !["plano", "etiquetas"].includes(tab) || plan.key === piecesKey) return;
    if (!pieces.length) {
      setPlan({ result: null, loading: false, error: "Adicione peças para gerar o plano de corte.", key: piecesKey });
      return;
    }
    setPlan((o) => ({ ...o, loading: true, error: "" }));
    api.post("/optimize", { pieces, project_id: id, plan_mode: planMode, sheet_modes: sheetModes })
      .then((r) => setPlan({ result: r.data, loading: false, error: "", key: piecesKey }))
      .catch(async (e) => setPlan({ result: null, loading: false, error: await errorMessage(e), key: piecesKey }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, piecesKey, project]);

  const exportFile = async (kind, path, filename, body) => {
    let handle = null;
    if (isAdmin) {
      try {
        handle = await pickSaveTarget(filename);
      } catch {
        return;
      }
    }
    setBusy(kind);
    try {
      if (status !== "saved") await save();
      await downloadFile(path, filename, body, handle);
      toast.success(handle ? `Arquivo salvo: ${handle.name}` : "Download iniciado");
    } catch (e) {
      toast.error(await errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  if (!project) return <div className="min-h-screen grid place-items-center text-[#9CA3AF]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const n = slug(`${project.client || "cliente"}_${project.name}`);
  const st = statusOf(project);
  const setProjectStatus = async (next, msg) => {
    try {
      if (dirty.current) await save();
      await api.post(`/projects/${id}/status`, { status: next });
      setProject((o) => (latest.current = { ...o, status: next, completed: next !== "editando" }));
      toast.success(msg);
      if (!isAdmin && next === "enviado") nav("/");
    } catch (e) {
      toast.error(await errorMessage(e));
    }
  };
  const saveLayout = async (sheets) => {
    if (dirty.current) await save();
    try {
      const r = await api.put(`/projects/${id}/layout`, { sheets });
      setPlan({ result: r.data, loading: false, error: "", key: piecesKey });
      toast.success("Plano de corte ajustado salvo");
    } catch (e) {
      throw new Error(await errorMessage(e));
    }
  };
  const resetLayout = async () => {
    const r = await api.delete(`/projects/${id}/layout`);
    setPlan({ result: r.data, loading: false, error: "", key: piecesKey });
    toast.success("Plano automático restaurado");
  };
  const statusActions = isAdmin
    ? ({ enviado: [["conferido", "Conferido", "bg-sky-500 hover:bg-sky-600"]], aprovado: [["concluido", "Concluído", "bg-purple-500 hover:bg-purple-600"]], recusado: [["concluido", "Concluído", "bg-purple-500 hover:bg-purple-600"]] })[st] || []
    : ({ editando: [["enviado", "Enviar para a central", "bg-[#10B981] hover:bg-[#059669]"]], recusado: [["enviado", "Enviar para a central", "bg-[#10B981] hover:bg-[#059669]"]],
      conferido: [["aprovado", "Aprovar para corte", "bg-[#10B981] hover:bg-[#059669]"], ["recusado", "Recusar", "bg-red-500 hover:bg-red-600"]] })[st] || [];
  const locked = !isAdmin && CLIENT_LOCKED.includes(st);
  const sendBudget = async (body) => {
    if (dirty.current) await save();
    const r = await api.post(`/projects/${id}/budget/send`, body);
    setProject((o) => (latest.current = { ...o, sent_budget: r.data, budget_outdated: false }));
    toast.success("Orçamento enviado para a tela do cliente");
  };
  const setPieces = (ps) => update({ pieces: ps });
  const restoreCleared = () => {
    const back = clearedRef.current;
    if (!back) return;
    update({ pieces: [...back, ...(latest.current?.pieces || [])] });
    setCleared(null);
    toast.success(`Lista restaurada (${back.length} tipos de peça)`);
  };
  const savePiece = (p) => {
    const { _isNew, _index, ...clean } = p;
    const ps = [...pieces];
    if (_isNew) ps.push(clean); else ps[_index] = clean;
    setPieces(ps);
    setEditing(null);
    toast.success(_isNew ? "Peça adicionada" : "Peça atualizada");
  };

  const statusEl = {
    saved: <span className="flex items-center gap-1 text-[#10B981]"><Check className="w-3.5 h-3.5" /> Salvo</span>,
    saving: <span className="flex items-center gap-1 text-[#9CA3AF]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Salvando</span>,
    dirty: <span className="text-[#F39C12]">Alterações não salvas</span>,
    error: <span className="text-red-400">Erro ao salvar</span>,
  }[status];

  return (
    <div className="min-h-screen">
      <AppHeader>
        <div className="hidden md:flex items-center justify-end gap-2">
          {isAdmin && (
            <Button size="sm" data-testid="budget-btn" disabled={!!busy || !pieces.length} onClick={() => setBudgetOpen(true)} className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              <Calculator className="w-4 h-4 mr-1" /> Orçamento
            </Button>
          )}
          {(isAdmin || locked) && <Button size="sm" variant="outline" data-testid="export-pdf-btn" disabled={!!busy || !pieces.length} onClick={() => exportFile("pdf", `/projects/${id}/export/pdf`, `plano_corte_${n}.pdf`)} className="border-[#2D333F] bg-transparent hover:bg-[#21252F]">
            {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} PDF
          </Button>}
          {isAdmin && (
            <Button size="sm" variant="outline" data-testid="export-dxf-btn" title="ZIP com DXF separado por espessura e por chapa" disabled={!!busy || !pieces.length} onClick={() => exportFile("dxf", `/projects/${id}/export/dxf-zip`, `plano_corte_${n}_dxf.zip`)} className="border-[#2D333F] bg-transparent hover:bg-[#21252F]">
              {busy === "dxf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Baixar DXF
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" data-testid="export-gcode-btn" title="ZIP com G-code (.nc) por chapa para a Alca AL1928ATC" disabled={!!busy || !pieces.length} onClick={() => exportFile("gcode", `/projects/${id}/export/gcode-zip`, `plano_corte_${n}_gcode.zip`)} className="border-[#2D333F] bg-transparent hover:bg-[#21252F]">
              {busy === "gcode" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Baixar G-code (.nc)
            </Button>
          )}
          {isAdmin && (
          <Button size="sm" data-testid="export-labels-btn" disabled={!!busy || !pieces.length} onClick={() => exportFile("labels", `/projects/${id}/export/labels?logo=${labelLogo}`, `etiquetas_${n}.pdf`)} className="bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
            {busy === "labels" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Tag className="w-4 h-4 mr-1" />} Etiquetas
          </Button>
          )}
        </div>
      </AppHeader>
      <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1500px] mx-auto space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="back-to-projects" onClick={() => nav("/")} className="p-2 rounded-md hover:bg-[#21252F] text-[#9CA3AF]"><ArrowLeft className="w-5 h-5" /></button>
          <Input data-testid="project-client-input" disabled={locked} value={project.client} onChange={(e) => update({ client: e.target.value })} placeholder="Cliente" className="max-w-[260px] bg-transparent border-[#2D333F] font-heading text-xl font-bold h-11" />
          <Input data-testid="project-name-input" disabled={locked} value={project.name} onChange={(e) => update({ name: e.target.value })} placeholder="Ambiente" className="max-w-[260px] bg-transparent border-[#2D333F] font-heading text-xl h-11" />
          {!isAdmin && <StatusBadge status={st} testId="project-status-badge" />}
          {isAdmin && (
            <Select value={st} onValueChange={(v) => setProjectStatus(v, `Status: ${STATUS[v][0]}`)}>
              <SelectTrigger data-testid="admin-status-select" className="w-[180px] h-9 bg-[#0F1115] border-[#2D333F] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS).map(([k, [l]]) => <SelectItem key={k} value={k} data-testid={`admin-status-option-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <BudgetStatus at={project.sent_budget?.at} outdated={project.budget_outdated} onOpen={isAdmin ? () => setBudgetOpen(true) : null} testId="project-budget-status" />
          <div data-testid="save-status" className="text-xs font-mono ml-auto">{statusEl}</div>
          {statusActions.map(([to, label, cls]) => (
            <Button key={to} data-testid={`status-action-${to}`} disabled={status === "saving"}
              onClick={() => setProjectStatus(to, to === "enviado" ? "Projeto enviado para a central!" : `Status: ${STATUS[to][0]}`)}
              className={`${cls} text-black font-semibold`}>
              <Check className="w-4 h-4 mr-1" /> {label}
            </Button>
          ))}
        </div>
        {locked && (
          <div data-testid="project-locked-notice" className="rounded-lg border border-purple-500/50 bg-purple-500/10 px-4 py-3 text-sm text-purple-300">
            Projeto aprovado para corte. Este ambiente não pode mais ser alterado.
          </div>
        )}
        {!isAdmin && ["enviado", "conferido"].includes(st) && (
          <div data-testid="project-reviewed-notice" className="rounded-lg border border-sky-500/50 bg-sky-500/10 px-4 py-3 text-sm text-sky-300">
            {st === "conferido" ? "A marcenaria conferiu seu projeto. Aprove para corte ou recuse." : "Projeto enviado para a central."} Se você alterar algo, a marcenaria será avisada{st === "conferido" ? " e vai conferir de novo" : ""}.
          </div>
        )}
        {!isAdmin && project.sent_budget && (
          <div data-testid="client-budget-card" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-3">
            <p className="text-sm mr-auto">Orçamento da marcenaria: <span data-testid="client-budget-total" className="font-heading font-bold text-lg">{(+project.sent_budget.total).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span></p>
            <Button size="sm" data-testid="client-budget-pdf" onClick={() => exportFile("sent-budget", `/projects/${id}/sent-budget/pdf`, `orcamento_${n}.pdf`)}
              className="bg-red-600 hover:bg-red-700 text-white">Baixar orçamento (PDF)</Button>
          </div>
        )}
        <div className="flex md:hidden gap-2 flex-wrap">
          {isAdmin && <Button size="sm" data-testid="budget-btn-mobile" disabled={!!busy || !pieces.length} onClick={() => setBudgetOpen(true)} className="bg-red-600 text-white">Orçamento</Button>}
          {(isAdmin || locked) && <Button size="sm" variant="outline" data-testid="export-pdf-btn-mobile" disabled={!!busy || !pieces.length} onClick={() => exportFile("pdf", `/projects/${id}/export/pdf`, `plano_corte_${n}.pdf`)} className="border-[#2D333F] bg-transparent">PDF</Button>}
          {isAdmin && <Button size="sm" variant="outline" data-testid="export-dxf-btn-mobile" disabled={!!busy || !pieces.length} onClick={() => exportFile("dxf", `/projects/${id}/export/dxf-zip`, `plano_corte_${n}_dxf.zip`)} className="border-[#2D333F] bg-transparent">DXF</Button>}
          {isAdmin && <Button size="sm" variant="outline" data-testid="export-gcode-btn-mobile" disabled={!!busy || !pieces.length} onClick={() => exportFile("gcode", `/projects/${id}/export/gcode-zip`, `plano_corte_${n}_gcode.zip`)} className="border-[#2D333F] bg-transparent">G-code</Button>}
          {isAdmin && <Button size="sm" data-testid="export-labels-btn-mobile" disabled={!!busy || !pieces.length} onClick={() => exportFile("labels", `/projects/${id}/export/labels?logo=${labelLogo}`, `etiquetas_${n}.pdf`)} className="bg-[#E67E22] text-black">Etiquetas</Button>}
        </div>

        {!locked && <DefaultsBar defaults={project.defaults} onChange={(d) => update({ defaults: d })} />}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#181B21] border border-[#2D333F] h-11 p-1 flex-wrap h-auto">
            {[["modulos", "Módulos prontos"], ["pecas", "Lista de peças"], ["plano", "Plano de corte"], ...(isAdmin ? [["etiquetas", "Etiquetas"]] : [])].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="data-[state=active]:bg-[#E67E22] data-[state=active]:text-black px-4">{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="pecas" className="mt-6">
            <PiecesTable
              pieces={pieces}
              readOnly={locked}
              onAdd={() => setEditing({ ...newPiece(project.defaults, { name: "" }), _isNew: true })}
              onEdit={(i) => setEditing({ ...pieces[i], _index: i })}
              onDuplicate={(i) => { setPieces([...pieces.slice(0, i + 1), { ...newPiece({}, pieces[i]), name: `${pieces[i].name} (cópia)` }, ...pieces.slice(i + 1)]); toast.success("Peça duplicada"); }}
              onDelete={(i) => setPieces(pieces.filter((_, j) => j !== i))}
              onClear={() => { setCleared(pieces); setPieces([]); toast.success("Lista limpa", { action: { label: "Restaurar", onClick: () => restoreCleared() } }); }}
              onRestore={() => restoreCleared()} restoreCount={cleared?.length || 0}
            />
          </TabsContent>
          <TabsContent value="modulos" className="mt-6">
            {locked ? <p data-testid="modules-locked" className="text-sm text-[#9CA3AF]">Projeto aprovado para corte: não é possível adicionar módulos.</p> : (
            <ModulesGallery defaults={project.defaults} onAdd={(ps, name) => { setPieces([...pieces, ...ps]); toast.success(`${name} adicionado (${ps.length} tipos de peça)`); setTab("pecas"); }} />
            )}
          </TabsContent>
          <TabsContent value="plano" className="mt-6">
            <PlanModePicker value={planMode} stats={plan.result?.mode_stats} onChange={(m) => update({ plan_mode: m, sheet_modes: {} })} disabled={locked} />
            <CutPlan result={plan.result} pieces={plan.result?.pieces || pieces} loading={plan.loading} error={plan.error}
              sheetModes={sheetModes} onSheetMode={locked ? null : (n, m) => update({ sheet_modes: { ...sheetModes, [n]: m } })}
              canEdit={!locked} onSaveLayout={saveLayout} onResetLayout={resetLayout} />
          </TabsContent>
          {isAdmin && <TabsContent value="etiquetas" className="mt-6">
            <LabelsView result={plan.result} pieces={plan.result?.pieces || pieces} project={{ name: project.name, client: project.client }} loading={plan.loading} error={plan.error}
              logo={labelLogo} onLogo={toggleLogo}
              canDownload={isAdmin} downloading={busy === "labels"} onDownload={() => exportFile("labels", `/projects/${id}/export/labels?logo=${labelLogo}`, `etiquetas_${n}.pdf`)} />
          </TabsContent>}
        </Tabs>
      </main>
      <PieceDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} piece={editing} onSave={savePiece} />
      {isAdmin && (
        <BudgetDialog open={budgetOpen} onOpenChange={setBudgetOpen} projectId={id}
          ensureSaved={async () => { if (dirty.current) await save(); }}
          onSend={sendBudget}
          onDownload={(body) => exportFile("budget", `/projects/${id}/budget/pdf`, `orcamento_${n}.pdf`, body)} />
      )}
    </div>
  );
}
